import React, { useState } from 'react';
import { ExternalLink, Loader2, Check, X, KeyRound, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from '../hooks/use-toast';

// The most common way to fail at Idolaris was to succeed at everything else
// first. You signed up, picked a recipe, wired a product, waited for the render,
// and only then were told "No provider is set up to make images" — nineteen of
// the first sixty four failures on this instance, every one of them after the
// person had already done the work. The key was always the first requirement and
// the last thing mentioned.
//
// So it is mentioned first. One provider is enough; the rest can wait until
// there is a reason to care about them.

export default function FirstRun({ providers, onDone, onSkip }) {
  const [id, setId] = useState(providers[0]?.id || '');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const chosen = providers.find((p) => p.id === id) || providers[0];

  // Save then test, in that order and in one press. Testing a key the server has
  // not stored yet would check the wrong thing, and asking somebody to press
  // Save, then Test, then work out what the two answers together mean is asking
  // them to debug our plumbing.
  const connect = async () => {
    const key = value.trim();
    if (!key) return;
    setBusy(true);
    setResult(null);
    try {
      await api.saveMyKeys({ provider_keys: { [id]: key } });
      const res = await api.testMyKey(id);
      setResult(res);
      if (res.ok) {
        await api.finishOnboarding();
        toast({ title: 'You are set up', description: `${chosen.label} answered. Everything you make is billed to your own account.` });
        onDone?.();
      }
    } catch (e) {
      setResult({ ok: false, message: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#2A2A30] bg-[#141417] p-6 sm:p-8">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-node-model/15">
          <KeyRound size={16} className="text-node-model" />
        </span>
        <div>
          <h2 className="font-serif-display text-xl text-white">One thing before your first photo</h2>
          <p className="text-sm text-white/50">Takes about two minutes, once.</p>
        </div>
      </div>

      <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/70">
        Idolaris runs on your own provider account, so the images are yours and the bill is yours —
        nobody meters you and nothing you make touches another person&apos;s account. You need
        one key to start. Pick a provider, open their console, and paste the key back here.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        {providers.map((p) => (
          <button
            key={p.id}
            onClick={() => { setId(p.id); setResult(null); }}
            data-testid={`firstrun-pick-${p.id}`}
            className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${
              p.id === id
                ? 'bg-white text-black'
                : 'border border-[#2A2A30] text-white/60 hover:border-white/40 hover:text-white'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {chosen && (
        <>
          <p className="mt-4 max-w-xl text-[13px] leading-relaxed text-white/45">{chosen.note}</p>

          <a
            href={chosen.console_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="firstrun-console"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#2A2A30] px-4 py-2 text-sm text-white/85 transition-colors hover:border-white/40"
          >
            Open {chosen.label} and create a key <ExternalLink size={13} />
          </a>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <input
              type="password"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') connect(); }}
              placeholder={`Paste your ${chosen.label} key`}
              data-testid="firstrun-key"
              className="min-w-0 flex-1 rounded-md border border-[#2A2A30] bg-[#0E0E10] px-3 py-2.5 text-sm text-white/85 focus:border-node-model focus:outline-none"
            />
            <button
              onClick={connect}
              disabled={busy || !value.trim()}
              data-testid="firstrun-connect"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-medium text-black transition-colors hover:bg-white/90 disabled:opacity-40"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}
              {busy ? 'Checking…' : 'Connect'}
            </button>
          </div>

          {result && (
            <p
              data-testid="firstrun-result"
              className={`mt-3 flex items-start gap-1.5 text-[13px] ${result.ok ? 'text-emerald-300' : 'text-red-300'}`}
            >
              {result.ok ? <Check size={14} className="mt-0.5 shrink-0" /> : <X size={14} className="mt-0.5 shrink-0" />}
              {result.message}
            </p>
          )}
        </>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-[#2A2A30] pt-4">
        <button
          onClick={onSkip}
          data-testid="firstrun-skip"
          className="inline-flex items-center gap-1 text-sm text-white/45 transition-colors hover:text-white/80"
        >
          Look around first <ArrowRight size={13} />
        </button>
        <span className="text-[11px] text-white/30">
          You can add or change keys any time under API Keys.
        </span>
      </div>
    </div>
  );
}
